/** Configuration — Automation — jobs, schedules, retry, sample rules */

export const AUTOMATION_ROUTE = '/admin/configuration/automation';

export const AUTOMATION_MODULES = [
  { value: 'posts', label: 'Posts' },
  { value: 'reviews', label: 'Reviews' },
  { value: 'business_profiles', label: 'Business Profiles' },
];

export const RECORD_TYPE_OPTIONS = [
  { value: 'post', label: 'Post', module: 'posts' },
  { value: 'review', label: 'Review', module: 'reviews' },
  { value: 'business_profile_content', label: 'Business Profile Content', module: 'business_profiles' },
];

export const CONNECTED_CONFIG_OPTIONS = [
  {
    value: 'content_control',
    label: 'Content Control',
    reference: 'sample-cfg-content-control',
    available: true,
  },
];

/** Table 1 jobs only. Exact boundaries and registrations remain NOT CONFIRMED. */
export const AUTOMATED_JOB_OPTIONS = [
  {
    value: 'recheck_existing_post_content',
    label: 'Recheck Existing Post Content',
    reference: 'sample-job-recheck-post',
    meaning: 'Scheduled recheck of supported public, published Post content. Private Message/Quote conversations, private evidence and Account Setup context are excluded.',
    module: 'posts',
    recordType: 'post',
    connectedConfig: 'content_control',
    requiresConnectedConfig: true,
    requiresSchedule: true,
    supportsRetry: true,
    supportsConditions: true,
    schedules: ['every_7_days'],
    available: true,
  },
  {
    value: 'recheck_existing_review_content',
    label: 'Recheck Existing Review Content',
    reference: 'sample-job-recheck-review',
    meaning: 'Scheduled recheck of supported public, published Review content. Whether Business Reply is included remains NOT CONFIRMED.',
    module: 'reviews',
    recordType: 'review',
    connectedConfig: 'content_control',
    requiresConnectedConfig: true,
    requiresSchedule: true,
    supportsRetry: true,
    supportsConditions: true,
    schedules: ['every_7_days'],
    available: true,
  },
  {
    value: 'recheck_public_profile_content',
    label: 'Recheck Public Profile Content',
    reference: 'sample-job-recheck-profile',
    meaning: 'Scheduled recheck of supported public Business Profile content. Structured Contact Details and Allowed External Links are excluded from public-content rechecks under current definitions.',
    module: 'business_profiles',
    recordType: 'business_profile_content',
    connectedConfig: 'content_control',
    requiresConnectedConfig: true,
    requiresSchedule: true,
    supportsRetry: true,
    supportsConditions: true,
    schedules: ['every_7_days'],
    available: true,
  },
];

/** @deprecated No Rule State in Automation. Kept for legacy callers. */
export const RULE_STATE_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
];

/** Every 7 days = fixed elapsed interval of 168 hours (confirmed). Other choices NOT CONFIRMED. */
export const SCHEDULE_OPTIONS = [
  {
    value: 'every_7_days',
    label: 'Every 7 days',
    meaning: 'Fixed elapsed interval of 168 hours. First occurrence due 168 hours after the starting point; later occurrences follow the same interval.',
  },
];

export const RETRY_ATTEMPT_OPTIONS = [
  { value: '0', label: '0' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
];

export const RETRY_INTERVAL_OPTIONS = [
  { value: '15_min', label: '15 minutes' },
  { value: '30_min', label: '30 minutes' },
  { value: '1_hour', label: '1 hour' },
  { value: '4_hours', label: '4 hours' },
  { value: '24_hours', label: '24 hours' },
];

/** Optional Condition catalogue — NOT CONFIRMED. Keep empty until confirmed. */
export const CONDITION_FIELD_OPTIONS = [];

export const CONDITION_OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Equals', multi: false },
  { value: 'does_not_equal', label: 'Does Not Equal', multi: false },
  { value: 'is_any_of', label: 'Is Any Of', multi: true },
  { value: 'is_not_any_of', label: 'Is Not Any Of', multi: true },
];

export const CONDITION_VALUE_OPTIONS = {};

const LEGACY_MODULE_MAP = {
  customers: 'posts',
  customer: 'posts',
  businesses: 'business_profiles',
  business: 'business_profiles',
  passwords: 'posts',
  compliance: 'business_profiles',
  messages: 'posts',
  quotes: 'reviews',
  support_tickets: 'reviews',
  billing_subscriptions: 'business_profiles',
  moderation: 'reviews',
  fraud_security: 'posts',
  teams_permissions: 'business_profiles',
};

const LEGACY_STATE_MAP = {
  draft: 'paused',
  inactive: 'paused',
};

export function labelFromOptions(options, value) {
  if (value == null || value === '') return '';
  return options.find((option) => option.value === value)?.label ?? String(value);
}

export function namesMatch(a, b) {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

export function operatorIsMulti(operator) {
  return CONDITION_OPERATOR_OPTIONS.find((option) => option.value === operator)?.multi === true;
}

export function normalizeAutomationModule(value) {
  if (!value) return '';
  if (AUTOMATION_MODULES.some((option) => option.value === value)) return value;
  return LEGACY_MODULE_MAP[value] || value;
}

export function normalizeRecordType(value) {
  if (!value) return '';
  if (RECORD_TYPE_OPTIONS.some((option) => option.value === value)) return value;
  const legacy = {
    automation_type: '',
    reminder: 'post',
    review_prompt: 'review',
  };
  return legacy[value] || value;
}

export function normalizeAutomatedJob(value) {
  if (!value) return '';
  if (AUTOMATED_JOB_OPTIONS.some((option) => option.value === value)) return value;
  const legacy = {
    review_prompt: 'recheck_existing_post_content',
    reminder: 'recheck_existing_post_content',
    expiry_check: 'recheck_public_profile_content',
  };
  return legacy[value] || value;
}

export function normalizeConnectedConfig(value) {
  if (!value || value === 'not_required') return '';
  return value;
}

export function normalizeRuleState(value) {
  if (!value) return '';
  const mapped = LEGACY_STATE_MAP[String(value).toLowerCase()] || String(value).toLowerCase();
  return RULE_STATE_OPTIONS.some((option) => option.value === mapped) ? mapped : '';
}

export function normalizeSchedule(value) {
  if (!value || value === 'not_applicable') return '';
  return SCHEDULE_OPTIONS.some((option) => option.value === value) ? value : value;
}

export function normalizeRetryAttempts(value) {
  if (value == null || value === '') return '';
  return String(value);
}

export function getAutomatedJobDefinition(value) {
  return AUTOMATED_JOB_OPTIONS.find((option) => option.value === normalizeAutomatedJob(value)) || null;
}

export function getConnectedConfigDefinition(value) {
  return CONNECTED_CONFIG_OPTIONS.find((option) => option.value === normalizeConnectedConfig(value)) || null;
}

export function getRecordTypesForModule(moduleValue) {
  const module = normalizeAutomationModule(moduleValue);
  return RECORD_TYPE_OPTIONS.filter((option) => option.module === module);
}

export function getAutomatedJobsForScope(moduleValue, recordType) {
  const module = normalizeAutomationModule(moduleValue);
  const type = normalizeRecordType(recordType);
  if (!module || !type) return [];
  return AUTOMATED_JOB_OPTIONS.filter((job) => job.module === module && job.recordType === type && job.available !== false);
}

export function getConnectedConfigsForJob(automatedJob) {
  const job = getAutomatedJobDefinition(automatedJob);
  if (!job?.requiresConnectedConfig) return [];
  return CONNECTED_CONFIG_OPTIONS.filter((option) => (
    (!job.connectedConfig || option.value === job.connectedConfig) && option.available !== false
  ));
}

export function jobRequiresConnectedConfig(automatedJob) {
  return Boolean(getAutomatedJobDefinition(automatedJob)?.requiresConnectedConfig);
}

export function jobRequiresSchedule(automatedJob) {
  return Boolean(getAutomatedJobDefinition(automatedJob)?.requiresSchedule);
}

export function jobSupportsRetry(automatedJob) {
  return Boolean(getAutomatedJobDefinition(automatedJob)?.supportsRetry);
}

export function jobSupportsConditions(automatedJob) {
  return Boolean(getAutomatedJobDefinition(automatedJob)?.supportsConditions);
}

export function isAutomatedJobAvailable(automatedJob) {
  const job = getAutomatedJobDefinition(automatedJob);
  return Boolean(job && job.available !== false);
}

export function isConnectedConfigAvailable(connectedConfig) {
  if (!connectedConfig) return true;
  const config = getConnectedConfigDefinition(connectedConfig);
  return Boolean(config && config.available !== false);
}

export function isScheduleAvailable(schedule) {
  if (!schedule) return true;
  return SCHEDULE_OPTIONS.some((option) => option.value === schedule);
}

export function getScheduleOptionsForJob(automatedJob) {
  const job = getAutomatedJobDefinition(automatedJob);
  if (!job?.requiresSchedule) return [];
  const allowed = job.schedules || SCHEDULE_OPTIONS.map((option) => option.value);
  return SCHEDULE_OPTIONS.filter((option) => allowed.includes(option.value));
}

export function getScheduleMeaning(value) {
  return SCHEDULE_OPTIONS.find((option) => option.value === value)?.meaning || '';
}

export function getAutomatedJobMeaning(value) {
  return getAutomatedJobDefinition(value)?.meaning || '';
}

export function getReferenceAvailabilityLabel(automatedJob, connectedConfig, schedule) {
  const jobOk = !automatedJob || isAutomatedJobAvailable(automatedJob);
  const configOk = isConnectedConfigAvailable(connectedConfig);
  const scheduleOk = !schedule || isScheduleAvailable(schedule);
  return jobOk && configOk && scheduleOk ? 'Available' : 'Unavailable';
}

export function getAvailableConditionFields(form = {}) {
  if (!jobSupportsConditions(form.automatedJob)) return [];
  return CONDITION_FIELD_OPTIONS;
}

export function getConditionValueOptions(field) {
  return CONDITION_VALUE_OPTIONS[field] || [];
}

export function buildAutomationScopeKey(rule) {
  const job = getAutomatedJobDefinition(rule.automatedJob);
  const connected = job?.requiresConnectedConfig
    ? (normalizeConnectedConfig(rule.connectedConfig) || job.connectedConfig || '')
    : '';
  return [
    normalizeAutomationModule(rule.module) || '',
    normalizeRecordType(rule.recordType) || '',
    normalizeAutomatedJob(rule.automatedJob) || '',
    connected,
  ].join('|');
}

export function findDuplicateAutomationRule(rules, candidate, excludeId) {
  const key = buildAutomationScopeKey(candidate);
  if (!candidate.module || !candidate.recordType || !candidate.automatedJob) return null;
  return (rules || []).find((rule) => rule.id !== excludeId && buildAutomationScopeKey(rule) === key) || null;
}

export function nextAutomationRuleId(rules) {
  const nums = (rules || []).map((rule) => {
    const match = String(rule.id || '').match(/^AUT-(\d+)$/i);
    return match ? parseInt(match[1], 10) : 0;
  });
  const max = nums.length ? Math.max(...nums) : 0;
  return `AUT-${String(max + 1).padStart(4, '0')}`;
}

export function createEmptyCondition() {
  return { id: `cond-${Date.now()}`, field: '', operator: '', values: [] };
}

export function createEmptyAutomationForm() {
  return {
    id: '',
    ruleName: '',
    module: '',
    recordType: '',
    automatedJob: '',
    connectedConfig: '',
    schedule: '',
    maxRetryAttempts: '',
    retryInterval: '',
    conditions: [],
    changeReason: '',
    dateAdded: '',
    addedBy: '',
    lastUpdated: '',
    lastUpdatedBy: '',
  };
}

function normalizeCondition(raw) {
  const values = Array.isArray(raw?.values)
    ? raw.values.filter(Boolean)
    : raw?.value
      ? [raw.value]
      : [];
  return {
    id: raw?.id || `cond-${Math.random().toString(36).slice(2, 8)}`,
    field: raw?.field || '',
    operator: raw?.operator || '',
    values,
  };
}

export function applyJobDerivedFields(form) {
  const job = getAutomatedJobDefinition(form.automatedJob);
  const next = { ...form };
  if (!job) return next;
  if (job.requiresConnectedConfig && !next.connectedConfig && job.connectedConfig) {
    next.connectedConfig = job.connectedConfig;
  }
  if (!job.requiresConnectedConfig) next.connectedConfig = '';
  if (!job.requiresSchedule) next.schedule = '';
  else {
    const schedules = getScheduleOptionsForJob(job.value);
    if (schedules.length === 1) next.schedule = schedules[0].value;
  }
  if (!job.supportsRetry) {
    next.maxRetryAttempts = '';
    next.retryInterval = '';
  } else if (String(next.maxRetryAttempts) === '0') {
    next.retryInterval = '';
  }
  if (!job.supportsConditions) next.conditions = [];
  return next;
}

export function applyAutomationSelectionChange(form, field, value) {
  let next = { ...form, [field]: value };
  if (field === 'module') {
    next.recordType = '';
    next.automatedJob = '';
    next.connectedConfig = '';
    next.schedule = '';
    next.maxRetryAttempts = '';
    next.retryInterval = '';
    next.conditions = [];
  }
  if (field === 'recordType') {
    next.automatedJob = '';
    next.connectedConfig = '';
    next.schedule = '';
    next.maxRetryAttempts = '';
    next.retryInterval = '';
    next.conditions = [];
  }
  if (field === 'automatedJob') {
    next.connectedConfig = '';
    next.schedule = '';
    next.maxRetryAttempts = '';
    next.retryInterval = '';
    next.conditions = [];
  }
  if (field === 'maxRetryAttempts' && String(value) === '0') {
    next.retryInterval = '';
  }
  return applyJobDerivedFields(next);
}

export function normalizeAutomationForm(raw) {
  if (!raw) return createEmptyAutomationForm();
  const module = normalizeAutomationModule(raw.module);
  const recordType = normalizeRecordType(raw.recordType || raw.automationType);
  const automatedJob = normalizeAutomatedJob(raw.automatedJob || raw.automationType);
  const job = getAutomatedJobDefinition(automatedJob);
  const derived = applyJobDerivedFields({
    ...createEmptyAutomationForm(),
    ...raw,
    module,
    recordType: recordType || job?.recordType || '',
    automatedJob,
    connectedConfig: normalizeConnectedConfig(raw.connectedConfig) || job?.connectedConfig || '',
    schedule: normalizeSchedule(raw.schedule || raw.scheduleDefinition),
    maxRetryAttempts: normalizeRetryAttempts(raw.maxRetryAttempts ?? raw.retryAttempts),
    retryInterval: raw.retryInterval || '',
    conditions: Array.isArray(raw.conditions) ? raw.conditions.map(normalizeCondition) : [],
  });
  return {
    ...createEmptyAutomationForm(),
    ...raw,
    ...derived,
    ruleName: raw.ruleName || '',
    changeReason: raw.changeReason || '',
    dateAdded: raw.dateAdded || raw.createdOn || '',
    addedBy: raw.addedBy || raw.createdBy || '',
    lastUpdated: raw.lastUpdated || raw.modifiedOn || '',
    lastUpdatedBy: raw.lastUpdatedBy || raw.modifiedBy || '',
  };
}

export function formatConditionLine(condition) {
  const field = getConditionFieldLabel(condition.field);
  const operator = getConditionOperatorLabel(condition.operator);
  const values = (condition.values || []).map((value) => getConditionValueLabel(condition.field, value));
  return `${field} · ${operator} · ${values.join(', ')}`;
}

export function formatConditionsPreview(conditions = [], automatedJob) {
  if (automatedJob && !jobSupportsConditions(automatedJob)) {
    return { text: 'Not Applicable', extraCount: 0, lines: [], unsupported: true };
  }
  if (!conditions.length) return { text: 'No Conditions', extraCount: 0, lines: [] };
  const lines = conditions.map(formatConditionLine);
  if (lines.length === 1) return { text: lines[0], extraCount: 0, lines };
  return {
    text: `${lines[0]} · +${lines.length - 1} more`,
    extraCount: lines.length - 1,
    lines,
  };
}

export function formatConnectedConfigPreview(rule) {
  const job = getAutomatedJobDefinition(rule.automatedJob);
  if (!job?.requiresConnectedConfig) return 'Not Required';
  const config = getConnectedConfigDefinition(rule.connectedConfig);
  if (!rule.connectedConfig) return '—';
  if (!config) return 'Unavailable';
  if (config.available === false) return 'Unavailable';
  return config.label;
}

export function formatSchedulePreview(rule) {
  const job = getAutomatedJobDefinition(rule.automatedJob);
  if (!job?.requiresSchedule) return 'Not Applicable';
  if (!rule.schedule) return '—';
  if (!isScheduleAvailable(rule.schedule)) return 'Unavailable';
  return getScheduleLabel(rule.schedule);
}

export function formatRetryPreview(rule) {
  const job = getAutomatedJobDefinition(rule.automatedJob);
  if (!job?.supportsRetry) return 'Not Applicable';
  const max = Number(rule.maxRetryAttempts);
  if (!Number.isFinite(max) || rule.maxRetryAttempts === '') return '—';
  if (max === 0) return 'No retry';
  const interval = getRetryIntervalLabel(rule.retryInterval) || rule.retryInterval || '—';
  return `${max} ${max === 1 ? 'retry' : 'retries'} · ${interval}`;
}

export function formatAutomationDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = date.getDate();
  const month = date.toLocaleString('en-GB', { month: 'short' });
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const suffix = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${day} ${month} ${year}, ${hours}:${minutes} ${suffix}`;
}

export function sortAutomationRules(rules = []) {
  return [...rules].sort((a, b) => {
    const aTime = new Date(a.lastUpdated || a.dateAdded || 0).getTime();
    const bTime = new Date(b.lastUpdated || b.dateAdded || 0).getTime();
    return bTime - aTime;
  });
}

export function getAutomationModuleLabel(value) {
  return labelFromOptions(AUTOMATION_MODULES, normalizeAutomationModule(value));
}

export function getRecordTypeLabel(value) {
  return labelFromOptions(RECORD_TYPE_OPTIONS, normalizeRecordType(value));
}

export function getAutomatedJobLabel(value) {
  return labelFromOptions(AUTOMATED_JOB_OPTIONS, normalizeAutomatedJob(value));
}

export function getConnectedConfigLabel(value) {
  if (!value) return 'Not Required';
  return labelFromOptions(CONNECTED_CONFIG_OPTIONS, normalizeConnectedConfig(value)) || String(value);
}

export function getConnectedConfigReference(value) {
  return getConnectedConfigDefinition(value)?.reference || '';
}

export function getAutomatedJobReference(value) {
  return getAutomatedJobDefinition(value)?.reference || '';
}

export function getRuleStateLabel(value) {
  return labelFromOptions(RULE_STATE_OPTIONS, normalizeRuleState(value));
}

export function getScheduleLabel(value) {
  return labelFromOptions(SCHEDULE_OPTIONS, value);
}

export function getRetryAttemptsLabel(value) {
  return labelFromOptions(RETRY_ATTEMPT_OPTIONS, normalizeRetryAttempts(value));
}

export function getRetryIntervalLabel(value) {
  return labelFromOptions(RETRY_INTERVAL_OPTIONS, value);
}

export function getConditionFieldLabel(value) {
  return labelFromOptions(CONDITION_FIELD_OPTIONS, value);
}

export function getConditionOperatorLabel(value) {
  return labelFromOptions(CONDITION_OPERATOR_OPTIONS, value);
}

export function getConditionValueLabel(field, value) {
  return labelFromOptions(getConditionValueOptions(field), value) || String(value || '');
}

function actorFields(stamp, name) {
  return {
    addedBy: name,
    lastUpdatedBy: name,
    dateAdded: stamp,
    lastUpdated: stamp,
  };
}

export function buildSampleAutomationRules() {
  return [
    {
      id: 'AUT-0001',
      ruleName: 'Post Content Recheck',
      module: 'posts',
      recordType: 'post',
      automatedJob: 'recheck_existing_post_content',
      connectedConfig: 'content_control',
      schedule: 'every_7_days',
      maxRetryAttempts: '1',
      retryInterval: '15_min',
      conditions: [],
      changeReason: 'Add a scheduled recheck of supported public, published Post content.',
      ...actorFields('2026-08-12T09:15:00+10:00', 'Alex Morgan'),
    },
    {
      id: 'AUT-0002',
      ruleName: 'Review Content Recheck',
      module: 'reviews',
      recordType: 'review',
      automatedJob: 'recheck_existing_review_content',
      connectedConfig: 'content_control',
      schedule: 'every_7_days',
      maxRetryAttempts: '1',
      retryInterval: '15_min',
      conditions: [],
      changeReason: 'Add a scheduled recheck of supported public, published Review content.',
      ...actorFields('2026-08-12T09:30:00+10:00', 'Priya Shah'),
    },
    {
      id: 'AUT-0003',
      ruleName: 'Profile Content Recheck',
      module: 'business_profiles',
      recordType: 'business_profile_content',
      automatedJob: 'recheck_public_profile_content',
      connectedConfig: 'content_control',
      schedule: 'every_7_days',
      maxRetryAttempts: '1',
      retryInterval: '15_min',
      conditions: [],
      changeReason: 'Add a scheduled recheck of supported public Business Profile content.',
      ...actorFields('2026-08-12T09:45:00+10:00', 'Daniel Lee'),
    },
  ].map((rule) => applyJobDerivedFields(normalizeAutomationForm(rule)));
}

/** @deprecated aliases for previous Automation catalogues */
export const AUTOMATION_TYPE_OPTIONS = AUTOMATED_JOB_OPTIONS.map((job) => ({ value: job.value, label: job.label }));
export const AUTOMATION_TRIGGER_SOURCE_OPTIONS = CONNECTED_CONFIG_OPTIONS.map((option) => ({ value: option.value, label: option.label }));
export const AUTOMATION_RESULT_OPTIONS = RULE_STATE_OPTIONS;
export const AUT_RUN_TIMING_OPTIONS = SCHEDULE_OPTIONS;
export const AUT_STOP_CONDITION_OPTIONS = [];
export const AUT_REVIEW_REQUIREMENT_OPTIONS = [];
export const AUT_TIMING_UNIT_OPTIONS = RETRY_INTERVAL_OPTIONS;
export const AUTOMATION_STATUS_OPTIONS = RULE_STATE_OPTIONS;

export function generateNextAutomationRuleId(rules) {
  return nextAutomationRuleId(rules);
}

export function getAutomationTypeLabel(value) {
  return getAutomatedJobLabel(value);
}

export function getAutomationTriggerSourceLabel(value) {
  return getConnectedConfigLabel(value);
}

export function getAutomationResultLabel(value) {
  return getRuleStateLabel(value);
}

export function getAutomationRunTimingLabel(value) {
  return getScheduleLabel(value);
}

export function getAutomationStopConditionLabel() {
  return '';
}

export function getAutomationReviewRequirementLabel() {
  return '';
}

export function normalizeAutomationType(value) {
  return normalizeAutomatedJob(value);
}

export function normalizeAutomationTriggerSource(value) {
  return normalizeConnectedConfig(value);
}

export function normalizeAutomationResult(value) {
  return normalizeRuleState(value);
}

export function normalizeAutomationRunTiming(value) {
  return normalizeSchedule(value);
}

export function normalizeAutomationStopCondition() {
  return '';
}

export function normalizeAutomationReviewRequirement() {
  return '';
}

export function normalizeAutomationStatus(value) {
  return normalizeRuleState(value);
}

export function normalizeAutomationRuleScope(module, automationType) {
  const job = getAutomatedJobDefinition(automationType);
  return {
    module: normalizeAutomationModule(module),
    automationType: normalizeAutomatedJob(automationType),
    recordType: job?.recordType || '',
  };
}

export function getAutomationTypesForModule(module) {
  const moduleValue = normalizeAutomationModule(module);
  return AUTOMATED_JOB_OPTIONS.filter((job) => job.module === moduleValue).map((job) => ({
    value: job.value,
    label: job.label,
  }));
}

export function getTriggerSourcesForSelection() {
  return CONNECTED_CONFIG_OPTIONS;
}

export function getTriggerConditions() {
  return [];
}

export function getAutomationResultsForType() {
  return RULE_STATE_OPTIONS;
}

export function getStopConditionsForSelection() {
  return [];
}

export function getReviewRequirementsForResult() {
  return [];
}

export function requiresDurationTiming() {
  return false;
}

export function requiresCalendarTiming(schedule) {
  return jobRequiresSchedule(schedule) || SCHEDULE_OPTIONS.some((option) => option.value === schedule);
}

export function automationRuleDuplicateKey(rule) {
  return buildAutomationScopeKey(rule);
}

export function automationRuleConflictKey(rule) {
  return buildAutomationScopeKey(rule);
}

export function findConflictingAutomationRule() {
  return null;
}

export function getAutomationRules() {
  return buildSampleAutomationRules();
}

export function saveAutomationRules() {}

export const getSurfacesForModule = () => [];
export const getTargetInputsForSurface = () => [];
export const getTriggerTypesForSelection = () => [];
export const getTriggerConditionOptions = () => [];
export const getAutomationSurfaceLabel = () => '';
export const getAutomationTargetInputLabel = () => '';
export const getAutomationTriggerTypeLabel = () => '';
